import { useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ROUTES } from 'shared/constants/routes';
import { ArrowLeft, ArrowRight, Check, WandSparkles, X } from 'lucide-react';
import { Card } from 'shared/components/ui/Card';
import { AUTOPILOT_PARAM, DEMO_ARENA, pause, typeInto } from 'shared/demo/autopilot';
import { useAutopilot } from 'shared/demo/useAutopilot';
import { AutopilotStatus } from 'shared/demo/AutopilotStatus';
import { useArenaOnboarding } from 'modules/arena/onboarding/useArenaOnboarding';
import { OnboardingStepper } from 'modules/arena/onboarding/components/OnboardingStepper';
import { ArenaLivePreview } from 'modules/arena/onboarding/components/ArenaLivePreview';
import { Button } from 'shared/components/ui/Button';
import { ONBOARDING_STEPS } from 'modules/arena/onboarding/content';
import { StepArenaInfo } from 'modules/arena/onboarding/steps/StepArenaInfo';
import { StepSports } from 'modules/arena/onboarding/steps/StepSports';
import { StepCourts } from 'modules/arena/onboarding/steps/StepCourts';
import { StepSchedule } from 'modules/arena/onboarding/steps/StepSchedule';
import { StepTeam } from 'modules/arena/onboarding/steps/StepTeam';
import { StepCancellation } from 'modules/arena/onboarding/steps/StepCancellation';
import { StepPlan } from 'modules/arena/onboarding/steps/StepPlan';
import { StepReview } from 'modules/arena/onboarding/steps/StepReview';
import { BrandLogo } from 'shared/components/brand/BrandLogo';

const STEP_DETAILS = [
  { title: 'Sobre a arena', description: 'Nome, unidade e endereço que os jogadores vão ver.' },
  { title: 'Esportes', description: 'As modalidades que a sua arena oferece.' },
  { title: 'Quadras', description: 'Cada quadra, os esportes permitidos e o preço por hora.' },
  { title: 'Horário', description: 'Dias e horários em que a agenda fica aberta para reservas.' },
  { title: 'Equipe', description: 'Gestores, recepção e instrutores com as permissões certas.' },
  { title: 'Cancelamento', description: 'Até quando o jogador pode cancelar e quanto recebe de volta.' },
  { title: 'Plano', description: 'A mensalidade da plataforma. Dá para trocar depois.' },
  { title: 'Revisão', description: 'Confira tudo antes de colocar a arena no ar.' },
];

const STEP_COMPONENTS = [
  StepArenaInfo,
  StepSports,
  StepCourts,
  StepSchedule,
  StepTeam,
  StepCancellation,
  StepPlan,
  StepReview,
];

export function ArenaOnboardingPage() {
  const state = useArenaOnboarding();
  const CurrentStep = STEP_COMPONENTS[state.stepIndex];
  const autopilot = useAutopilot();
  const [searchParams] = useSearchParams();
  // O roteiro sempre chama a versão mais recente do estado (o "Concluir" lê os campos atuais).
  const stateRef = useRef(state);
  stateRef.current = state;

  function simulateOnboarding() {
    const s = () => stateRef.current;
    const goNext = async (signal: AbortSignal) => {
      await pause(650, signal);
      s().next();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      await pause(450, signal);
    };

    autopilot.run(async (signal, report) => {
      s().setStepIndex(0);
      await pause(400, signal);
      report('Nome da arena');
      await typeInto((value) => s().setArenaName(value), DEMO_ARENA.arenaName, signal, 'arenaName');
      report('Unidade');
      await typeInto((value) => s().setUnitName(value), DEMO_ARENA.unitName, signal, 'unitName');
      report('Endereço');
      await typeInto((value) => s().setAddress(value), DEMO_ARENA.address, signal, 'address', 18);
      await goNext(signal);

      report('Esportes oferecidos');
      for (const sport of DEMO_ARENA.extraSports) {
        if (!s().selectedSportIds.includes(sport)) s().toggleSport(sport);
        await pause(450, signal);
      }
      await goNext(signal);

      report('Cadastrando as quadras');
      for (let count = 1; count <= DEMO_ARENA.courts.length; count++) {
        s().replaceCourts(DEMO_ARENA.courts.slice(0, count));
        await pause(650, signal);
      }
      await goNext(signal);

      report('Horário de funcionamento');
      await pause(900, signal);
      await goNext(signal);

      report('Convidando a equipe');
      for (const member of DEMO_ARENA.team) {
        s().addTeamMember(member.name, member.role);
        await pause(550, signal);
      }
      await goNext(signal);

      report('Política de cancelamento');
      await pause(900, signal);
      await goNext(signal);

      report('Escolhendo o plano');
      s().setPlanId(DEMO_ARENA.planId);
      await pause(900, signal);
      await goNext(signal);

      report('Revisando os dados');
      await pause(2000, signal);
      report('Publicando a arena');
      await s().finish();
    });
  }

  const autoStarted = useRef(false);
  useEffect(() => {
    if (searchParams.get(AUTOPILOT_PARAM) && !autoStarted.current) {
      autoStarted.current = true;
      simulateOnboarding();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // O que falta para avançar em cada etapa (null = pode seguir). Só as etapas com dado obrigatório.
  const blockers = [
    state.arenaName.trim() ? null : 'Informe o nome da arena para continuar.',
    state.selectedSportIds.length ? null : 'Escolha pelo menos um esporte.',
    state.courts.some((court) => court.name.trim()) ? null : 'Cadastre pelo menos uma quadra com nome.',
  ];
  const blocker = blockers[state.stepIndex] ?? null;
  // Pelas etapas do topo dá para voltar livremente, mas avançar só até a primeira etapa pendente.
  const firstBlocked = blockers.findIndex(Boolean);
  const lastReachable = firstBlocked === -1 ? ONBOARDING_STEPS.length - 1 : firstBlocked;
  const selectStep = (index: number) => {
    if (index <= lastReachable) state.setStepIndex(index);
  };

  const backButton = !state.isFirstStep && (
    <Button variant="neutral" onClick={state.back} disabled={autopilot.isRunning}>
      <ArrowLeft className="h-4 w-4" aria-hidden="true" />
      Voltar
    </Button>
  );

  const primaryButton = state.isLastStep ? (
    <Button onClick={state.finish} disabled={state.isSubmitting || autopilot.isRunning} className="px-5">
      {state.isSubmitting ? 'Publicando...' : 'Concluir cadastro'}
      {!state.isSubmitting && <Check className="h-4 w-4" aria-hidden="true" />}
    </Button>
  ) : (
    <Button onClick={state.next} disabled={Boolean(blocker) || autopilot.isRunning} className="px-5">
      Avançar
      <ArrowRight className="h-4 w-4" aria-hidden="true" />
    </Button>
  );

  const stepLabel = `Etapa ${state.stepIndex + 1} de ${ONBOARDING_STEPS.length}`;
  const currentStep = STEP_DETAILS[state.stepIndex];

  return (
    <div className="min-h-screen bg-background">
      <AutopilotStatus label={autopilot.label} onStop={autopilot.stop} />

      {/* Barra própria do onboarding: só o essencial para a pessoa terminar a configuração. */}
      <header className="sticky top-0 z-30 border-b border-border/60 bg-surface/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <div className="flex items-center gap-3">
            <Link to={ROUTES.HOME} aria-label="Marcô, ir para o início">
              <BrandLogo />
            </Link>
            <span className="hidden h-5 w-px bg-border sm:block" />
            <span className="hidden text-sm font-medium text-ink-secondary sm:block">Configuração da arena</span>
          </div>
          <Link
            to={ROUTES.HOME}
            className="flex items-center gap-1.5 text-sm font-medium text-ink-secondary transition-colors hover:text-ink"
          >
            <X className="h-4 w-4" aria-hidden="true" />
            Salvar e sair
          </Link>
        </div>
        {/* Celular: progresso em uma barra fina no lugar das etapas. */}
        <div className="h-1 bg-chip lg:hidden">
          <motion.div
            className="h-full bg-primary"
            initial={false}
            animate={{ width: `${((state.stepIndex + 1) / ONBOARDING_STEPS.length) * 100}%` }}
            transition={{ duration: 0.4 }}
          />
        </div>
      </header>

      <div className="mx-auto grid max-w-7xl gap-8 px-4 pb-32 pt-8 sm:px-6 md:pb-12 lg:grid-cols-[240px_minmax(0,1fr)] xl:grid-cols-[240px_minmax(0,1fr)_320px] xl:gap-10">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <OnboardingStepper
              steps={STEP_DETAILS}
              currentIndex={state.stepIndex}
              lastReachable={lastReachable}
              onSelect={selectStep}
            />
          </div>
        </aside>

        <main className="min-w-0">
          <p className="text-sm font-medium text-primary-dark">{stepLabel}</p>
          <h1 className="mt-1 font-heading text-3xl font-bold text-ink">{currentStep.title}</h1>
          <p className="mt-2 text-ink-secondary">{currentStep.description}</p>

          <Card className="mt-6 p-0">
            <motion.div
              key={state.stepIndex}
              className="p-6 sm:p-8"
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.25 }}
            >
              <CurrentStep {...state} />
            </motion.div>

            {/* Desktop: ações logo abaixo do formulário, onde o olho já está. */}
            <div className="hidden items-center justify-between gap-3 border-t border-border/60 px-6 py-4 sm:px-8 md:flex">
              <div>{backButton}</div>
              <div className="flex items-center gap-4">
                {blocker && <span className="text-sm text-ink-secondary">{blocker}</span>}
                {primaryButton}
              </div>
            </div>
          </Card>

          {!autopilot.isRunning && (
            <button
              type="button"
              onClick={simulateOnboarding}
              className="mt-4 flex items-center gap-2 text-sm text-ink-secondary transition-colors hover:text-primary-dark"
            >
              <WandSparkles className="h-4 w-4" aria-hidden="true" />
              <span>
                <span className="font-medium">Modo demonstração:</span> preencher automaticamente
              </span>
            </button>
          )}
        </main>

        <aside className="hidden xl:block">
          <div className="sticky top-24">
            <ArenaLivePreview state={state} />
          </div>
        </aside>
      </div>

      {/* Celular: barra fixa no rodapé (o formulário é longo e o polegar fica embaixo). */}
      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-surface/95 px-4 py-3 backdrop-blur md:hidden">
        {blocker && <p className="mb-2 text-center text-xs text-ink-secondary">{blocker}</p>}
        <div className="flex items-center gap-3">
          {backButton}
          <div className="flex-1 [&>button]:w-full">{primaryButton}</div>
        </div>
      </div>
    </div>
  );
}
